import { Card, CardContent } from "@/components/ui/card";
import { Info } from "lucide-react";
import { useTestResults } from "@/hooks/useTestResults";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { EmptyMilestones } from "./milestones/EmptyMilestones";
import { GradeLevelCard } from "./milestones/GradeLevelCard";
import { gradeLevelOrder, processTestResults } from "@/utils/milestoneUtils";

export const MilestonesSection = ({ childId }: { childId: string }) => {
  const { data: testResults, isLoading: isLoadingResults } = useTestResults(childId);

  // Fetch all available tests for comparison
  const { data: availableTests, isLoading: isLoadingTests } = useQuery({
    queryKey: ["allTests", childId],
    queryFn: async () => {
      const { data: child } = await supabase
        .from("child_profiles")
        .select("age")
        .eq("id", childId)
        .maybeSingle();

      if (!child) return [];

      // Convert age to appropriate grade level format
      const ageLevel = child.age === 0 ? "Infant" : `Age ${child.age}`;

      const { data: tests } = await supabase
        .from("tests")
        .select("*")
        .lte("grade_level", ageLevel);

      return tests || [];
    },
    enabled: !!childId,
  });

  if (isLoadingResults || isLoadingTests) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Loading milestones...</p>
        </CardContent>
      </Card>
    );
  }

  if (!testResults?.length || !availableTests?.length) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Milestones will appear here as your child completes tests. Milestones are awarded for:
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Completing all tests in a specific category (e.g., all Mathematics tests for Age 1)</li>
                <li>Stage milestone: Complete all tests across a subject for a specific age</li>
              </ul>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const gradeLevelCompletions = processTestResults(availableTests, testResults);

  // Filter out incomplete milestones and sort by grade level order
  const completedGradeLevels = Object.values(gradeLevelCompletions)
    .filter(gradeLevel => gradeLevel.completed || gradeLevel.categories.some(cat => cat.completed))
    .sort((a, b) => {
      const indexA = gradeLevelOrder.indexOf(a.gradeLevel);
      const indexB = gradeLevelOrder.indexOf(b.gradeLevel);
      return indexA - indexB;
    });

  if (completedGradeLevels.length === 0) {
    return <EmptyMilestones />;
  }

  return (
    <div className="space-y-6">
      {completedGradeLevels.map((gradeLevel) => (
        <GradeLevelCard 
          key={gradeLevel.gradeLevel} 
          gradeLevel={gradeLevel}
        />
      ))}
    </div>
  );
};